import { GoogleGenerativeAI } from "@google/generative-ai";

const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
const delay = ms => new Promise(resolve => setTimeout(resolve, ms));


export const generateLogicalQuestion = async () => {
  try {
    const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash' });

    const prompt = {
      contents: [
        {
          parts: [
            {
              text: `Generate a random logical reasoning question that tests analytical thinking and problem-solving skills. 
Return the question in JSON format like this:
{
  "question": "How can you cut a cake into 8 equal pieces using only 3 cuts?"
}

Respond ONLY with the JSON object, no additional text.`
            }
          ]
        }
      ]
    };

    const result = await model.generateContent(prompt);

    // Extract text from the result safely
    let responseText = await result.response.text();

    console.log('AI Response:', responseText);

    // Clean and parse JSON properly
    const jsonMatch = responseText.match(/{[\s\S]*}/);
    if (jsonMatch) {
      const jsonResponse = JSON.parse(jsonMatch[0]);
      return jsonResponse.question;
    } else {
      throw new Error('Invalid JSON format in AI response');
    }
  } catch (error) {
    console.error('Error generating logical question:', error);
    return 'How would you cut a cake into 8 equal pieces using only 3 cuts?'; // Fallback question
  }
};

export const analyzeLogicalAnswer = async (answer) => {
  try {
    const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash' });

    const prompt = {
      contents: [
        {
          parts: [
            {
              text: `You are a logical reasoning evaluator. Analyze the candidate's response and score it out of 100. Provide JSON output only:
{
  "score": 85,
  "feedback": "Good logical thinking, but the approach can be improved."
}

Analyze this response:
Candidate's Response: "${answer}"

Respond ONLY with the JSON object, no additional text.`
            }
          ]
        }
      ]
    };

    const result = await model.generateContent(prompt);
    let responseText = result.response.text().trim();

    // Extract JSON if wrapped in ```json
    if (responseText.includes('```')) {
      responseText = responseText.match(/```(?:json)?\s*([\s\S]*?)\s*```/)[1];
    }

    // Clean unwanted characters and parse JSON
    responseText = responseText.replace(/^[^{]*/, '').replace(/[^}]*$/, '');
    const response = JSON.parse(responseText);

    return response;
  } catch (error) {
    console.error('Logical answer analysis error:', error);
    return {
      score: 0,
      feedback: 'Failed to analyze answer. Please try again later.'
    };
  }
};


export const analyzeResume = async (resumeText, { jobRequirements, candidateProfile }) => {
  try {
    const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash' });

    const prompt = {
      contents: [{
        parts: [{
          text: `You are an ATS analyzer. Analyze this candidate's profile and resume against the job requirements. Return ONLY a JSON object with the following structure:
{
  "skillsMatch": {
    "score": 75,
    "matchingSkills": ["JavaScript", "React"],
    "missingSkills": ["Python"]
  },
  "experienceMatch": {
    "score": 80,
    "relevantExperience": ["Frontend Developer"],
    "gapsIdentified": ["Leadership experience"]
  },
  "certificationsMatch": {
    "score": 60,
    "matchingQualifications": ["Bachelor's Degree"],
    "recommendedCertifications": ["AWS Certification"]
  },
  "totalScore": 72,
  "explanation": "Strong technical skills but needs cloud certifications",
  "atsScores": {
    "skills": 75,
    "experience": 80,
    "certifications": 60,
    "total": 72
  },
  "atsExplanation": {
    "strengths": ["Strong technical background", "Relevant work experience"],
    "weaknesses": ["Missing certifications", "Leadership gap"],
    "recommendations": ["Obtain cloud certifications", "Take leadership courses"]
  }
}

Analyze this data:
Job Requirements: ${JSON.stringify(jobRequirements)}
Candidate Profile: ${JSON.stringify(candidateProfile)}
Resume Text: ${resumeText}

Remember: Respond ONLY with the JSON object, no other text.`
        }]
      }]
    };

    const result = await model.generateContent(prompt);
    let responseText = result.response.text().trim();
    
    if (responseText.includes('```')) {
      responseText = responseText.match(/```(?:json)?\s*([\s\S]*?)\s*```/)[1];
    }
    
    responseText = responseText.replace(/^[^{]*/g, '').replace(/[^}]*$/g, '');
    
    const response = JSON.parse(responseText);
    
    return {
      ...response,
      atsScores: {
        skills: response.skillsMatch.score,
        experience: response.experienceMatch.score,
        certifications: response.certificationsMatch.score,
        total: response.totalScore
      },
      atsExplanation: {
        strengths: response.skillsMatch.matchingSkills,
        weaknesses: [...response.skillsMatch.missingSkills, ...response.experienceMatch.gapsIdentified],
        recommendations: response.certificationsMatch.recommendedCertifications
      },
      canApply: response.totalScore >= 50
    };
  } catch (error) {
    console.error('Resume analysis error:', error);
    return {
      skillsMatch: { score: 0, matchingSkills: [], missingSkills: [] },
      experienceMatch: { score: 0, relevantExperience: [], gapsIdentified: [] },
      certificationsMatch: { score: 0, matchingQualifications: [], recommendedCertifications: [] },
      totalScore: 0,
      explanation: "Failed to analyze resume",
      atsScores: { skills: 0, experience: 0, certifications: 0, total: 0 },
      atsExplanation: { strengths: [], weaknesses: [], recommendations: [] },
      canApply: false
    };
  }
};

// import { DeepSeekAI } from "@deepseek/sdk";  // Replace with actual SDK if available

// const deepAI = new DeepSeekAI(process.env.deep);
// const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

// export const analyzeResume = async (resumeText, requirements) => {
//   const maxRetries = 3;
//   let retries = 0;

//   while (retries < maxRetries) {
//     try {
//       // Assume DeepSeek AI has a similar method to get a model and generate content
//       const model = deepAI.getModel({ model: "DeepSeek-V3" });

//       // Build the prompt for DeepSeek
//       const prompt = `
//       You are an ATS (Applicant Tracking System) analyzer. Review this resume against the job requirements.
      
//       Job Requirements:
//       ${requirements}
      
//       Resume Text:
//       ${resumeText}
      
//       Analyze the resume and provide a JSON response with exactly these fields:
//       {
//         "score": (a number between 0-100),
//         "explanation": (a detailed explanation of matching and missing skills),
//         "meetsMinimumRequirements": (true or false),
//         "matchedSkills": (list of matched requirements),
//         "missingSkills": (list of missing requirements)
//       }
      
//       Consider partial matches and related experience. If skills are present but experience duration is unclear, provide partial scoring.`;

//       // Assuming `generateContent` is how you invoke the model in DeepSeek
//       const result = await model.generateContent({ prompt });

//       // Assuming DeepSeek API gives a similar response structure
//       const responseText = result.response.text.trim();
//       const cleanedResponse = responseText.replace(/```json\s*|\s*```/g, '').trim();
//       const response = JSON.parse(cleanedResponse);

//       // Allow applications with partial matches
//       const canApply = response.score >= 0;

//       return {
//         ...response,
//         canApply,
//         suggestion: !canApply ? "Consider adding more details about your experience duration and highlighting specific technical skills." : null
//       };

//     } catch (error) {
//       console.error('Error analyzing resume:', error);
//       if (error.message.includes("503") && retries < maxRetries) {
//         retries++;
//         console.log(`Retrying... Attempt ${retries} of ${maxRetries}`);
//         await delay(2000); // Wait for 2 seconds before retry
//       } else {
//         return {
//           score: 0,
//           explanation: error.message || "Error analyzing resume",
//           meetsMinimumRequirements: false,
//           canApply: false,
//           matchedSkills: [],
//           missingSkills: [],
//           suggestion: "Please ensure your resume clearly states your years of experience and technical skills."
//         };
//       }
//     }
//   }
// };

export const generateAptitudeQuestions = async (promptData) => {
  try {
    const { position, companyName, requirements, description } = promptData;
    const model = genAI.getGenerativeModel({ model: "gemini-2.0-flash" });
    
    const prompt = `Generate 15 technical multiple choice questions for a ${position} position at ${companyName}.
    Requirements: ${requirements.join(', ')}
    Job Description: ${description}

    Return the response as a valid JSON array of question objects. Each question object should have:
    - id (string)
    - text (string with the question)
    - options (array of 4 strings)
    - correctAnswer (string matching one of the options)

    Format: [{"id": "q1", "text": "question text", "options": ["A", "B", "C", "D"], "correctAnswer": "A"}, ...]`;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text();
    
    // Clean the response text before parsing
    const cleanedText = text.replace(/```json\s*|\s*```/g, '').trim();
    
    try {
      const questions = JSON.parse(cleanedText);
      if (!Array.isArray(questions) || questions.length !== 15) {
        throw new Error('Invalid question format');
      }
      return questions;
    } catch (parseError) {
      console.error('JSON parsing error:', parseError);
      throw new Error('Failed to parse questions');
    }

  } catch (error) {
    console.error('Error generating questions:', error);
    // Return default questions if generation fails
    return Array(15).fill(null).map((_, i) => ({
      id: `q${i + 1}`,
      text: `Technical Question ${i + 1}`,
      options: ['Option A', 'Option B', 'Option C', 'Option D'],
      correctAnswer: 'Option A'
    }));
  }
};
